import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, api, isNetworkError } from './api';

/* ------------------------------ local cache ------------------------------- */

interface CacheEntry<T> {
  v: T;
  at: number;
}

const PREFIX = 'uroute.driver.cache.';

export function readCache<T>(key: string): CacheEntry<T> | null {
  try {
    const raw = localStorage.getItem(PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CacheEntry<T>;
    return parsed && typeof parsed.at === 'number' ? parsed : null;
  } catch {
    return null;
  }
}

export function writeCache<T>(key: string, value: T): void {
  try {
    localStorage.setItem(PREFIX + key, JSON.stringify({ v: value, at: Date.now() }));
  } catch {
    /* quota exceeded ??? cache is a nicety, never fatal */
  }
}

export function dropCache(key: string): void {
  try {
    localStorage.removeItem(PREFIX + key);
  } catch {
    /* ignore */
  }
}

/** Wipe every cached read (called on sign-out). */
export function clearAllCaches(): void {
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(PREFIX)) keys.push(k);
    }
    for (const k of keys) localStorage.removeItem(k);
  } catch {
    /* ignore */
  }
}

/* ------------------------------- invalidation ----------------------------- */

const invalidationListeners = new Set<(prefix: string) => void>();

/** Tell every query whose key starts with `prefix` to refetch (silent). */
export function invalidate(prefix: string): void {
  for (const fn of invalidationListeners) fn(prefix);
}

/* --------------------------------- hook ----------------------------------- */

export interface QueryResult<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
  refreshing: boolean;
  /** True when the last attempt failed because the device is offline. */
  offline: boolean;
  updatedAt: number | null;
  refresh: () => Promise<void>;
}

function messageFor(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (isNetworkError(err)) return "Can't reach UR-ROUTE right now.";
  return err instanceof Error ? err.message : 'Something went wrong.';
}

/**
 * Stale-while-revalidate reads: cached data paints instantly, the network
 * refreshes it in the background, and a service-worker broadcast triggers a
 * silent refetch when the runtime cache changed elsewhere.
 */
export function useApiQuery<T>(
  key: string,
  path: string,
  opts: { enabled?: boolean } = {},
): QueryResult<T> {
  const enabled = opts.enabled !== false;
  interface QueryState<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
  refreshing: boolean;
  offline: boolean;
  updatedAt: number | null;
}
const [state, setState] = useState<QueryState<T>>(() => {
    const cached = enabled ? readCache<T>(key) : null;
    return {
      data: cached?.v ?? null,
      error: null as string | null,
      loading: enabled && cached === null,
      refreshing: false,
      offline: false,
      updatedAt: cached?.at ?? null,
    };
  });

  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const refresh = useCallback(async (): Promise<void> => {
    if (!enabled) return;
    setState((s) => ({ ...s, refreshing: true }));
    try {
      const fresh = (await api.get<T>(path)) as T;
      if (!alive.current) return;
      writeCache(key, fresh);
      setState({
        data: fresh,
        error: null,
        loading: false,
        refreshing: false,
        offline: false,
        updatedAt: Date.now(),
      });
    } catch (err) {
      if (!alive.current) return;
      const offline = isNetworkError(err);
      setState((s) => ({
        ...s,
        error: offline ? null : messageFor(err),
        loading: false,
        refreshing: false,
        offline,
      }));
    }
  }, [key, path, enabled]);

  useEffect(() => {
    if (enabled) void refresh();
  }, [enabled, refresh]);

  /* Service worker told us the runtime cache for this URL changed. */
  useEffect(() => {
    if (!enabled || typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
    const onMessage = (event: MessageEvent) => {
      const data = event.data as
        | { type?: string; meta?: string; payload?: { updatedURL?: string; updatedUrl?: string } }
        | undefined;
      /* workbox posts { type: 'CACHE_UPDATED', meta: 'workbox-broadcast-update', payload: { updatedURL } }. */
      if (data?.type !== 'CACHE_UPDATED' && data?.meta !== 'workbox-broadcast-update') return;
      const updated = data.payload?.updatedURL ?? data.payload?.updatedUrl;
      if (!updated) return;
      const url = updated.replace(window.location.origin, '');
      const base = path.split('?')[0] ?? path;
      if (url === path || url === base || url.startsWith(`${base}?`)) void refresh();
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, [path, enabled, refresh]);

  /* Mutation elsewhere in the app invalidates this key. */
  useEffect(() => {
    const onInvalidate = (prefix: string) => {
      if (key.startsWith(prefix)) void refresh();
    };
    invalidationListeners.add(onInvalidate);
    return () => {
      invalidationListeners.delete(onInvalidate);
    };
  }, [key, refresh]);

  return { ...state, refresh };
}
